import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useNutsAuth } from '../../context/NutsAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const LOGO_URL =
  '/api/static/brand/logo-h0.png';

const LANGS = [
  { code: 'ca', label: 'CA' },
  { code: 'es', label: 'ES' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
];

const Input = ({ label, type = 'text', value, onChange, required, autoComplete, testid, helper, placeholder }) => (
  <label className="block">
    <span className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] block mb-2">
      {label} {required && <span className="text-[#D4AF37]">*</span>}
    </span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      autoComplete={autoComplete}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full bg-transparent border-b border-[#A8B4A6]/40 focus:border-[#D4AF37] outline-none py-2 text-[#F2E8D5] font-sans transition-colors duration-300"
    />
    {helper && <span className="text-[10px] text-[#A8B4A6]/70 mt-1 block">{helper}</span>}
  </label>
);

const LangSwitcher = () => {
  const { i18n } = useTranslation();
  return (
    <div className="fixed top-6 right-6 md:top-8 md:right-10 z-30 flex gap-3 md:gap-4 font-sans text-[11px] md:text-xs tracking-[0.25em]">
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => i18n.changeLanguage(l.code)}
          className={`transition-colors duration-300 ${
            i18n.language === l.code ? 'text-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5]'
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const BackToH0 = () => (
  <Link
    to="/"
    data-testid="nuts-back-h0"
    className="fixed top-6 left-6 md:top-8 md:left-10 z-30 group flex items-center gap-2 font-sans text-[10px] md:text-xs tracking-[0.32em] text-[#A8B4A6] hover:text-[#D4AF37] transition-colors uppercase"
  >
    <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
    H0
  </Link>
);

const RegisterForm = ({ onSwitch }) => {
  const { t } = useTranslation();
  const { register } = useNutsAuth();
  const [avatars, setAvatars] = useState([]);
  const [form, setForm] = useState({
    email: '',
    password: '',
    nickname: '',
    avatar_url: '',
    full_name: '',
    surnames: '',
    mobile: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/nuts/avatars`).then((r) => {
      setAvatars(r.data.avatars || []);
      setForm((f) => ({ ...f, avatar_url: r.data.avatars?.[0] || '' }));
    });
  }, []);

  const update = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.avatar_url) {
      toast.error(t('nuts.errors.pickAvatar'));
      return;
    }
    setSubmitting(true);
    try {
      await register(form);
      toast.success(t('nuts.welcome'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('nuts.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="nuts-register-form">
      <div className="grid md:grid-cols-2 gap-5">
        <Input label={t('nuts.form.nickname')} value={form.nickname} onChange={update('nickname')} required testid="nuts-input-nickname" helper={t('nuts.form.nicknameHelp')} />
        <Input label={t('nuts.form.email')} type="email" value={form.email} onChange={update('email')} required autoComplete="email" testid="nuts-input-email" />
        <Input label={t('nuts.form.password')} type="password" value={form.password} onChange={update('password')} required autoComplete="new-password" testid="nuts-input-password" helper={t('nuts.form.passwordHelp')} />
        <Input label={t('nuts.form.mobile')} type="tel" value={form.mobile} onChange={update('mobile')} testid="nuts-input-mobile" helper={t('nuts.form.privateHelp')} />
        <Input label={t('nuts.form.fullName')} value={form.full_name} onChange={update('full_name')} testid="nuts-input-fullname" helper={t('nuts.form.privateHelp')} />
        <Input label={t('nuts.form.surnames')} value={form.surnames} onChange={update('surnames')} testid="nuts-input-surnames" helper={t('nuts.form.privateHelp')} />
      </div>

      {/* Avatar gallery */}
      <div>
        <span className="font-sans text-[10px] tracking-[0.32em] uppercase text-[#A8B4A6] block mb-3">
          {t('nuts.form.chooseAvatar')} <span className="text-[#D4AF37]">*</span>
        </span>
        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-3 max-h-[440px] overflow-y-auto pr-2" data-testid="nuts-avatar-gallery">
          {avatars.map((url, idx) => {
            const selected = form.avatar_url === url;
            return (
              <button
                key={url}
                type="button"
                onClick={() => update('avatar_url')(url)}
                data-testid={`nuts-avatar-${idx + 1}`}
                className={`relative aspect-square rounded-full overflow-hidden border-2 transition-all duration-300 ${
                  selected
                    ? 'border-[#D4AF37] scale-105 shadow-[0_0_24px_rgba(212,175,55,0.4)]'
                    : 'border-[#A8B4A6]/20 hover:border-[#A8B4A6]/60 opacity-70 hover:opacity-100'
                }`}
              >
                <img src={url} alt="avatar" className="w-full h-full object-cover bg-[#083B1F]" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-4">
        <button
          type="button"
          onClick={onSwitch}
          className="font-sans text-[11px] tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37] transition-colors"
          data-testid="nuts-switch-to-login"
        >
          {t('nuts.haveAccount')} →
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="nuts-submit-register"
          className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-50 text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs px-8 py-3.5 rounded-full transition-all duration-500 shadow-[0_8px_30px_rgba(212,175,55,0.2)]"
        >
          {submitting ? t('nuts.submitting') : t('nuts.form.createAccount')}
          <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
        </button>
      </div>
    </form>
  );
};

const LoginForm = ({ onSwitch }) => {
  const { t } = useTranslation();
  const { login } = useNutsAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success(t('nuts.welcomeBack'));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('nuts.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-md" data-testid="nuts-login-form">
      <Input label={t('nuts.form.email')} type="email" value={email} onChange={setEmail} required autoComplete="email" testid="nuts-login-email" />
      <Input label={t('nuts.form.password')} type="password" value={password} onChange={setPassword} required autoComplete="current-password" testid="nuts-login-password" />
      <div className="flex items-center justify-between gap-4 pt-4">
        <button
          type="button"
          onClick={onSwitch}
          className="font-sans text-[11px] tracking-[0.32em] uppercase text-[#A8B4A6] hover:text-[#D4AF37] transition-colors"
          data-testid="nuts-switch-to-register"
        >
          {t('nuts.noAccount')} →
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="nuts-submit-login"
          className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E5C158] disabled:opacity-50 text-[#0E4D2A] font-sans font-medium tracking-[0.18em] uppercase text-xs px-8 py-3.5 rounded-full transition-all duration-500"
        >
          {submitting ? t('nuts.submitting') : t('nuts.form.signIn')}
          <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
        </button>
      </div>
    </form>
  );
};

export const NutsLanding = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('register'); // 'register' | 'login'

  return (
    <div className="relative min-h-screen w-full bg-[#0E4D2A] text-[#F2E8D5] overflow-x-hidden" data-testid="nuts-landing">
      {/* Watermark */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center select-none">
        <span
          className="font-serif font-black text-[#083B1F]"
          style={{ fontSize: 'min(75vw, 75vh)', lineHeight: 1, opacity: 0.35 }}
          aria-hidden="true"
        >
          H0
        </span>
      </div>

      <BackToH0 />
      <LangSwitcher />

      <div className="relative z-10 max-w-5xl mx-auto px-5 md:px-10 pt-24 md:pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center text-center"
        >
          <img src={LOGO_URL} alt="Hoyo Zero" className="w-20 sm:w-24 h-auto" />
          <p className="mt-8 text-[#D4AF37] font-sans font-light tracking-[0.32em] uppercase text-[10px] sm:text-xs">
            🥜 NUTS · ANDORRA
          </p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            {t('nuts.heroTitle')}
          </h1>
          <p className="mt-5 font-serif italic text-[#A8B4A6] text-lg md:text-xl max-w-2xl">
            {t('nuts.heroSubtitle')}
          </p>
        </motion.div>

        {/* Toggle */}
        <div className="mt-12 flex justify-center gap-8 font-sans text-[11px] md:text-xs tracking-[0.32em] uppercase">
          <button
            type="button"
            onClick={() => setMode('register')}
            data-testid="nuts-tab-register"
            className={`transition-colors duration-300 pb-2 border-b ${
              mode === 'register' ? 'text-[#D4AF37] border-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5] border-transparent'
            }`}
          >
            {t('nuts.tabRegister')}
          </button>
          <button
            type="button"
            onClick={() => setMode('login')}
            data-testid="nuts-tab-login"
            className={`transition-colors duration-300 pb-2 border-b ${
              mode === 'login' ? 'text-[#D4AF37] border-[#D4AF37]' : 'text-[#A8B4A6] hover:text-[#F2E8D5] border-transparent'
            }`}
          >
            {t('nuts.tabLogin')}
          </button>
        </div>

        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-10 max-w-3xl mx-auto bg-[#083B1F]/40 border border-[#D4AF37]/15 rounded-3xl p-6 md:p-10 backdrop-blur-sm"
        >
          {mode === 'register' ? (
            <RegisterForm onSwitch={() => setMode('login')} />
          ) : (
            <LoginForm onSwitch={() => setMode('register')} />
          )}
        </motion.div>

        <p className="mt-12 text-center text-[10px] tracking-[0.3em] uppercase text-[#A8B4A6]/60 font-sans">
          © {new Date().getFullYear()} · Hoyo Zero · @joan_monzo
        </p>
      </div>
    </div>
  );
};

export default NutsLanding;
